// Real-time camera frame quality analysis. Runs lightweight checks
// on video frames to provide live feedback while capturing.
// No external APIs - all processing in browser using canvas.

export const QUALITY_THRESHOLDS = {
  minLuminance: 30,               // mean grey (0-255). Below = too dark
  maxLuminance: 230,              // above = blown out / glare
  minSharpness: 50,               // Laplacian variance threshold
};

// Analyze luminance (brightness) and sharpness (blur detection).
// Uses downscaled image to stay fast on low-end phones.
export function analyzeLuma(imageData) {
  const { data, width, height } = imageData;
  const lum = new Float32Array(width * height);
  let sum = 0;
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    lum[j] = y;
    sum += y;
  }
  const mean = sum / lum.length;
  
  // 5-point Laplacian for edge detection (blur metric).
  // Sharp images have high variance; blurry ones have low variance.
  let lapSum = 0, lapSqSum = 0, count = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const v = -lum[i - width] - lum[i - 1] + 4 * lum[i] - lum[i + 1] - lum[i + width];
      lapSum += v;
      lapSqSum += v * v;
      count++;
    }
  }
  const lapMean = lapSum / count;
  const variance = lapSqSum / count - lapMean * lapMean;
  return { mean, variance };
}

// Analyze a video frame (canvas 2D context).
// Returns { blur, brightness, face } with message and severity (good/warn/bad).
export function analyzeFrame(canvas, isSelfie = false) {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  
  // Downscale to 256 px wide to keep analysis fast.
  const targetW = 256;
  const scale = targetW / canvas.width;
  const targetH = Math.max(1, Math.round(canvas.height * scale));
  
  const downscale = document.createElement('canvas');
  downscale.width = targetW;
  downscale.height = targetH;
  const dctx = downscale.getContext('2d', { willReadFrequently: true });
  dctx.drawImage(canvas, 0, 0, targetW, targetH);
  
  const imageData = dctx.getImageData(0, 0, targetW, targetH);
  const { mean, variance } = analyzeLuma(imageData);

  const result = { blur: null, brightness: null, face: null };

  // Check blur (sharpness).
  if (variance < QUALITY_THRESHOLDS.minSharpness) {
    result.blur = {
      severity: 'bad',
      message: 'Image is too blurry',
      variance,
    };
  } else if (variance < QUALITY_THRESHOLDS.minSharpness * 1.2) {
    result.blur = {
      severity: 'warn',
      message: 'Hold steady for sharper image',
      variance,
    };
  } else {
    result.blur = {
      severity: 'good',
      message: 'Good sharpness',
      variance,
    };
  }

  // Check brightness.
  if (mean < QUALITY_THRESHOLDS.minLuminance) {
    result.brightness = {
      severity: 'bad',
      message: 'Too dark - move to better light',
      luminance: mean,
    };
  } else if (mean > QUALITY_THRESHOLDS.maxLuminance) {
    result.brightness = {
      severity: 'bad',
      message: 'Too bright - reduce glare',
      luminance: mean,
    };
  } else if (mean < QUALITY_THRESHOLDS.minLuminance + 20) {
    result.brightness = {
      severity: 'warn',
      message: 'A bit dark',
      luminance: mean,
    };
  } else if (mean > QUALITY_THRESHOLDS.maxLuminance - 20) {
    result.brightness = {
      severity: 'warn',
      message: 'A bit bright',
      luminance: mean,
    };
  } else {
    result.brightness = {
      severity: 'good',
      message: 'Good lighting',
      luminance: mean,
    };
  }

  // Face detection for selfies.
  if (isSelfie) {
    const { detected, confidence } = detectFaceSimple(canvas);
    if (detected && confidence > 0.6) {
      result.face = {
        severity: 'good',
        message: 'Face detected ✓',
        detected: true,
        confidence,
      };
    } else if (detected && confidence > 0.3) {
      result.face = {
        severity: 'warn',
        message: 'Face unclear - try closer or straighter',
        detected: true,
        confidence,
      };
    } else {
      result.face = {
        severity: 'bad',
        message: 'No face detected',
        detected: false,
        confidence,
      };
    }
  } else {
    // For documents, we don't strictly require face detection
    result.face = {
      severity: 'unknown',
      message: null,
      detected: null,
    };
  }

  return result;
}

// Compute overall quality status from individual metrics.
// Returns { status, message, severity }.
export function computeQualityStatus(analysis, isSelfie = false, context = 'image') {
  const issues = [];
  let maxSeverity = 'good';

  if (analysis.blur?.severity === 'bad') {
    issues.push(analysis.blur.message);
    maxSeverity = 'bad';
  } else if (analysis.blur?.severity === 'warn') {
    issues.push(analysis.blur.message);
    if (maxSeverity !== 'bad') maxSeverity = 'warn';
  }

  if (analysis.brightness?.severity === 'bad') {
    issues.push(analysis.brightness.message);
    maxSeverity = 'bad';
  } else if (analysis.brightness?.severity === 'warn') {
    issues.push(analysis.brightness.message);
    if (maxSeverity !== 'bad') maxSeverity = 'warn';
  }

  if (isSelfie && analysis.face?.detected === false) {
    issues.push('No face detected');
    maxSeverity = 'bad';
  } else if (isSelfie && analysis.face?.severity === 'bad') {
    issues.push(analysis.face.message);
    maxSeverity = 'bad';
  }

  // Decide on primary message.
  let message = context === 'mrz' ? 'MRZ strip looks readable' : 'Good image';
  if (maxSeverity === 'bad') {
    message = issues.length > 0 ? issues[0] : 'Image quality too low';
  } else if (maxSeverity === 'warn') {
    message = issues.length > 0 ? issues[0] : 'Could be better';
  }

  return {
    status: maxSeverity === 'good' ? 'good' : maxSeverity === 'warn' ? 'caution' : 'bad',
    message,
    severity: maxSeverity,
    canCapture: maxSeverity !== 'bad',
  };
}

// Simple face detection using skin tone analysis + edge detection.
// This is fast and lightweight - no ML libraries needed.
// Returns { detected, confidence } where detected is boolean.
export function detectFaceSimple(canvas) {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  
  // Downscale to 128x128 for fast processing.
  const size = 128;
  const scale = size / Math.max(canvas.width, canvas.height);
  const w = Math.round(canvas.width * scale);
  const h = Math.round(canvas.height * scale);
  
  const thumb = document.createElement('canvas');
  thumb.width = size;
  thumb.height = size;
  const tctx = thumb.getContext('2d', { willReadFrequently: true });
  tctx.fillStyle = 'white';
  tctx.fillRect(0, 0, size, size);
  // Center the scaled image
  const x = (size - w) / 2;
  const y = (size - h) / 2;
  tctx.drawImage(canvas, x, y, w, h);
  
  const imgData = tctx.getImageData(0, 0, size, size);
  const data = imgData.data;
  
  // Count pixels that look like skin tones.
  // Skin tone heuristic: R > 95 && G > 40 && B > 20 && R > G && R > B
  // and not too saturated (variance between channels is moderate)
  let skinPixels = 0;
  let edgePixels = 0;
  
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    
    // Skin tone detection
    if (r > 95 && g > 40 && b > 20 && r > g && r > b) {
      const rg = Math.abs(r - g);
      const rb = Math.abs(r - b);
      if (rg < 50 && rb < 50) {
        skinPixels++;
      }
    }
  }
  
  // Check if we have enough skin-like pixels concentrated.
  const skinRatio = skinPixels / (size * size);
  
  // Also check for high-contrast regions (eyes, mouth, etc.)
  // by looking at edge density in the upper half (where eyes are).
  let edges = 0;
  for (let y = 0; y < size / 2; y++) {
    for (let x = 1; x < size - 1; x++) {
      const idx = (y * size + x) * 4;
      const r = data[idx], g = data[idx + 1], b = data[idx + 2];
      const r_left = data[idx - 4], g_left = data[idx - 3], b_left = data[idx - 2];
      const grayDiff = Math.abs(
        (0.299 * r + 0.587 * g + 0.114 * b) -
        (0.299 * r_left + 0.587 * g_left + 0.114 * b_left)
      );
      if (grayDiff > 30) edges++;
    }
  }
  
  const edgeRatio = edges / (size * size / 2);
  
  // Face detected if:
  // - Reasonable amount of skin tone (5-60%)
  // - Some edge features (eyes area)
  const faceDetected = skinRatio > 0.05 && skinRatio < 0.6 && edgeRatio > 0.05;
  const confidence = Math.min(
    1.0,
    (skinRatio * 0.6 + edgeRatio * 0.4) / 0.25 // normalize to ~1.0
  );
  
  return { detected: faceDetected, confidence };
}

// Get a 2D canvas from a video element for frame analysis.
export function captureVideoFrame(videoElement) {
  if (!videoElement || !videoElement.videoWidth) return null;
  const canvas = document.createElement('canvas');
  canvas.width = videoElement.videoWidth;
  canvas.height = videoElement.videoHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoElement, 0, 0);
  return canvas;
}
